import React, { useMemo } from 'react'
import { useCartShippingDiscountsQuery } from '@graphql/queries/__generated__/cart.generated'
import { CartTypeEnum } from '../../generated/gqlTypes'
import _ from 'lodash'
import FormattedPrice from '@components/FormattedPrice'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../apolloClient'
import { useCurrentCurrencyExchangeRateQuery } from '@graphql/queries/__generated__/shipping.generated'

const ShippingDiscountCard = () => {
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const { data } = useCartShippingDiscountsQuery({
    variables: { cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  const currencyExchangeRateQuery = useCurrentCurrencyExchangeRateQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  const [currency, rate] = useMemo(() => {
    if (!currencyExchangeRateQuery.data?.currentCurrencyExchangeRate) return ['USD', 1]
    return [
      currencyExchangeRateQuery.data.currentCurrencyExchangeRate.currencyCode,
      currencyExchangeRateQuery.data.currentCurrencyExchangeRate.rate,
    ]
  }, [currencyExchangeRateQuery.data])

  if (!data) return null

  const shippingDiscounts = data.cart?.shippingDiscounts || []
  const sortedDiscounts = _.sortBy(shippingDiscounts, 'value')
  const needSpent = sortedDiscounts.length
    ? sortedDiscounts[sortedDiscounts.length - 1].minValue - data.cart.totalPrice.discountUsd.gross
    : null
  if (!needSpent) return null

  const milestones = sortedDiscounts.map(discount => ({
    id: discount.id,
    minValue: discount.minValue,
    maxValue: discount.maxValue,
    value: discount.value,
    label:
      discount.value === 100 ? (
        <>
          Free Shipping for{' '}
          <FormattedPrice
            currency={currency}
            amount={discount.minValue * rate}
            options={{ maximumFractionDigits: 0 }}
          />
        </>
      ) : (
        <>
          -${discount.value}% for{' '}
          <FormattedPrice
            currency={currency}
            amount={discount.minValue * rate}
            options={{ maximumFractionDigits: 0 }}
          />
        </>
      ),
  }))

  const currentAmount = data.cart.totalPrice.discountUsd.gross

  const activeIndex = milestones.reduce((acc, m, index) => (currentAmount >= m.minValue ? index : acc), -1)
  const step = 100 / milestones.length

  const calculateProgress = () => {
    if (activeIndex === milestones.length - 1) {
      return 100
    } else if (activeIndex === -1) {
      const next = milestones[activeIndex + 1]
      const baseProgress = step / 2
      return ((currentAmount * 100) / next.minValue) * 0.01 * baseProgress
    } else if (activeIndex >= 0) {
      const prev = milestones[activeIndex]
      const next = milestones[activeIndex + 1]
      const baseProgress = step / 2 + activeIndex * step
      if (!next) {
        return baseProgress
      } else {
        const range = next.minValue - prev.minValue
        const passed = currentAmount - prev.minValue
        const ratio = Math.min(passed / range, 1)
        const maxOffset = step * 0.8
        return baseProgress + ratio * maxOffset
      }
    }
    return 0
  }

  return (
    <div className='fsp-horizontal'>
      <div className='fsp-card'>
        <div className='fsp-text'>
          {needSpent > 0 ? (
            <>
              You're only{' '}
              <span className='font-semibold'>
                <FormattedPrice currency={currency} amount={needSpent * rate} />{' '}
              </span>{' '}
              away from Free shipping
            </>
          ) : (
            'You unlocked Free Shipping'
          )}
        </div>

        <div className='fsp-track'>
          <div className='fsp-progress' style={{ width: `${calculateProgress()}%` }} />

          {milestones.map((m, index) => {
            const left = step / 2 + index * step
            const active = currentAmount >= m.minValue

            return (
              <React.Fragment key={m.id}>
                <div className={`fsp-dot ${active ? 'active' : ''}`} style={{ left: `${left}%` }} />

                <span className={`fsp-label ${active ? 'active' : ''}`} style={{ left: `${left}%` }}>
                  {m.label}
                </span>
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ShippingDiscountCard
